<template>
  <div class="advance-cod-modal advance-cod-wide-modal">
    <h4 class="modal-title">
      {{ isEdit ? $t("advanceCodWithdraw.form_title") : $t("advanceCodWithdraw.form_title") }}
    </h4>

    <div class="modal-body">
      <!-- ຊື່ຮ້ານ -->
      <div class="form-group">
        <label class="label">
          {{ $t("advanceCodWithdraw.shop_name") }} <span class="text-danger">*</span>
        </label>
        <input
          class="input input-text"
          type="text"
          :placeholder="$t('advanceCodWithdraw.enter_shop_name')"
          v-model="form.name"
        />
        <p class="error-text" v-if="errors.name">{{ errors.name }}</p>
      </div>

      <!-- ລູກຄ້າລົງທະບຽນ -->
      <div class="form-group">
        <label class="label">
          {{ $t("advanceCodWithdraw.registered_customer") }} <span class="text-danger">*</span>
        </label>
        <div class="muted-hint">{{ $t("advanceCodWithdraw.search_customer_hint") }}</div>

        <div class="search-input-wrap">
          <i class="fal fa-search search-icon"></i>
          <input
            class="input search-input"
            type="text"
            :placeholder="$t('advanceCodWithdraw.search_customer_placeholder')"
            v-model="customerSearch"
            @input="onSearchCustomers"
          />
        </div>

        <div class="customer-grid">
          <!-- Left: search results -->
          <div class="customer-panel">
            <div class="panel-header">
              <span>{{ $t("advanceCodWithdraw.search_results") }}</span>
              <span class="panel-count">{{ $t("advanceCodWithdraw.items_count", { n: filteredCustomers.length }) }}</span>
            </div>
            <div class="panel-body">
              <div
                v-for="c in filteredCustomers"
                :key="c.id"
                class="customer-row"
                :class="{ active: isSelected(c) }"
                @click="toggleCustomer(c)"
              >
                <div class="avatar">{{ (c.name || '?').charAt(0).toUpperCase() }}</div>
                <div class="info">
                  <div class="name">{{ c.name }}</div>
                  <div class="tel"><i class="fal fa-phone"></i> {{ c.tel }}</div>
                </div>
                <i v-if="isSelected(c)" class="fal fa-check-circle check-icon"></i>
              </div>
              <div v-if="!filteredCustomers.length" class="empty-state">
                {{ $t("advanceCodWithdraw.customer_not_found") }}
              </div>
            </div>
          </div>

          <!-- Right: selected -->
          <div class="customer-panel">
            <div class="panel-header">
              <i class="fal fa-user"></i>
              <span>{{ $t("advanceCodWithdraw.selected_customer") }}</span>
            </div>
            <div class="panel-body">
              <div v-if="form.customer" class="selected-row">
                <div class="avatar avatar-selected">
                  {{ (form.customer.name || '?').charAt(0).toUpperCase() }}
                </div>
                <div class="info">
                  <div class="name">{{ form.customer.name }}</div>
                  <div class="tel"><i class="fal fa-phone"></i> {{ form.customer.tel }}</div>
                </div>
                <i class="fal fa-times remove-icon" @click="clearCustomer"></i>
              </div>
              <div v-else class="empty-state center">
                {{ $t("advanceCodWithdraw.no_customer_selected") }}
              </div>
            </div>
          </div>
        </div>
        <p class="error-text" v-if="errors.customer">{{ errors.customer }}</p>
      </div>

      <!-- ສະຖານະຮ້ານ -->
      <div class="form-group status-group">
        <div>
          <label class="label">{{ $t("advanceCodWithdraw.shop_status") }} <span class="text-danger">*</span></label>
          <div class="muted-hint">{{ $t("advanceCodWithdraw.open_service") }}</div>
        </div>
        <label class="switch">
          <input type="checkbox" v-model="form.is_active" />
          <span class="slider"></span>
        </label>
      </div>

      <!-- ຖອນ COD ລ່ວງໜ້າ -->
      <div class="form-group">
        <label class="label">{{ $t("advanceCodWithdraw.withdraw_advance_cod") }}</label>
        <div class="cod-summary">
          <div class="summary-card success">
            <div class="summary-icon"><i class="fal fa-check-circle"></i></div>
            <div>
              <div class="summary-label">{{ $t("advanceCodWithdraw.completed_cod_total") }}</div>
              <div class="summary-value">{{ formatCurrency(completedCod) }} <span class="unit">{{ $t("common.ກີບ") }}</span></div>
            </div>
          </div>
          <div class="summary-card danger">
            <div class="summary-icon"><i class="fal fa-truck"></i></div>
            <div>
              <div class="summary-label">{{ $t("common.Delivering") }}</div>
              <div class="summary-value">{{ formatCurrency(pendingCod) }} <span class="unit">{{ $t("common.ກີບ") }}</span></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ກຳນົດ % ການຖອນ, ຄ່າທຳນຽມ -->
      <div class="form-row">
        <div class="form-group half">
          <label class="label">{{ $t("advanceCodWithdraw.set_withdraw_percent") }}</label>
          <div class="input-with-suffix">
            <input
              class="input"
              type="number"
              min="0"
              max="100"
              v-model="form.withdraw_percent"
              placeholder="0"
            />
            <span class="suffix">%</span>
          </div>
        </div>
        <div class="form-group half">
          <label class="label">{{ $t("advanceCodWithdraw.fee") }}</label>
          <div class="input-with-suffix">
            <input
              class="input"
              type="number"
              min="0"
              v-model="form.fee_percent"
              placeholder="0"
            />
            <span class="suffix">{{ $t("common.ກີບ") }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button
        class="btn-submit"
        :class="{ disabled: !canSubmit, loading: loading }"
        :disabled="!canSubmit || loading"
        @click="onSubmit"
      >
        {{ $t("advanceCodWithdraw.save_shop") }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  createAdvanceCodStore,
  updateAdvanceCodStore,
  fetchAdvanceCodCustomers,
} from '@/views/Corporate/AdvanceCodWithdraw/api';

export default {
  name: 'AdvanceCodAddEdit',
  props: {
    item: { type: Object, default: () => null },
  },
  data() {
    return {
      loading: false,
      customerSearch: '',
      customers: [],
      searchTimer: null,
      form: {
        name: '',
        customer: null,
        is_active: true,
        withdraw_percent: 0,
        fee_percent: 0,
      },
      errors: {
        name: '',
        customer: '',
      },
    };
  },
  computed: {
    isEdit() {
      return !!(this.item && this.item.id);
    },
    filteredCustomers() {
      return this.customers;
    },
    completedCod() {
      return this.item?.completed_cod ?? 24800000;
    },
    pendingCod() {
      return this.item?.pending_cod ?? 5600000;
    },
    canSubmit() {
      return !!(this.form.name && this.form.customer);
    },
  },
  watch: {
    'form.name'() { this.errors.name = ''; },
    'form.customer'() { this.errors.customer = ''; },
  },
  created() {
    if (this.isEdit) {
      this.form.name = this.item.name || '';
      this.form.customer = this.item.customer || null;
      this.form.is_active = !!this.item.is_active;
      this.form.withdraw_percent = this.item.withdraw_percent || 0;
      this.form.fee_percent = this.item.fee_percent || 0;
    }
    this.loadCustomers('');
  },
  methods: {
    loadCustomers(search) {
      fetchAdvanceCodCustomers(this.$http, { search }).then((res) => {
        this.customers = res.data.data || [];
      });
    },
    onSearchCustomers() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.loadCustomers(this.customerSearch);
      }, 200);
    },
    isSelected(c) {
      return this.form.customer && this.form.customer.id === c.id;
    },
    toggleCustomer(c) {
      this.form.customer = this.isSelected(c) ? null : { ...c };
    },
    clearCustomer() {
      this.form.customer = null;
    },
    formatCurrency(v) {
      const n = Number(v) || 0;
      return n.toLocaleString('en-US');
    },
    validate() {
      this.errors.name = this.form.name ? '' : this.$t('advanceCodWithdraw.enter_shop_name_required');
      this.errors.customer = this.form.customer ? '' : this.$t('advanceCodWithdraw.select_customer_required');
      return !this.errors.name && !this.errors.customer;
    },
    onSubmit() {
      if (!this.validate()) return;
      this.loading = true;

      const payload = {
        name: this.form.name,
        customer_id: this.form.customer?.id,
        customer: this.form.customer,
        is_active: this.form.is_active,
        withdraw_percent: Number(this.form.withdraw_percent) || 0,
        fee_percent: Number(this.form.fee_percent) || 0,
      };

      const request = this.isEdit
        ? updateAdvanceCodStore(this.$http, this.item.id, payload)
        : createAdvanceCodStore(this.$http, payload);

      request
        .then(() => {
          this.loading = false;
          this.$emit('success');
          this.$emit('close');
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.advance-cod-modal {
  padding: 6px 12px 10px;
  font-family: inherit;
  width: 100%;
}

.modal-title {
  text-align: center;
  font-weight: 700;
  font-size: 20px;
  color: #222;
  margin: 4px 0 20px;
}

.modal-body { padding: 0 4px; }

.form-group { margin-bottom: 16px; }
.form-row { display: flex; gap: 16px; }
.form-group.half { flex: 1; }

.label {
  font-weight: 500;
  color: #2c2f36;
  display: inline-block;
  margin-bottom: 6px;
}
.text-danger { color: #ef4444; }
.muted-hint { font-size: 12px; color: #8b8f99; margin-bottom: 8px; }
.error-text { color: #ef4444; font-size: 12px; margin-top: 4px; }

.input {
  width: 100%;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  padding: 0 12px;
  font-size: 14px;
  font-family: $font;
  background: #fff;
  &:focus { outline: none; border-color: #195BFF; }
}
.input-text::placeholder { color: #b3b8c2; }

.search-input-wrap {
  position: relative;
  margin-bottom: 10px;
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #8b8f99;
  }
  .search-input { padding-left: 36px; background: #f5f7fb; border: 1px solid #e6e9ef; }
}

.customer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.customer-panel {
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  height: 260px;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f5f7fb;
  border-bottom: 1px solid #eef0f4;
  font-size: 13px;
  color: #4b5161;
  i { margin-right: 6px; }
}
.panel-count { color: #8b8f99; font-size: 12px; }

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 6px 0;
}

.customer-row,
.selected-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  position: relative;
  &:hover { background: #f5f7fb; }
  &.active { background: #eef3ff; }
}

.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e6e9ef;
  color: #4b5161;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}
.avatar-selected { background: #dbe6ff; color: #195BFF; }

.info { flex: 1; min-width: 0; }
.name { font-size: 13px; color: #2c2f36; }
.tel { font-size: 12px; color: #8b8f99; }

.check-icon { color: #195BFF; }
.remove-icon {
  color: #8b8f99;
  cursor: pointer;
  padding: 4px;
  &:hover { color: #ef4444; }
}

.empty-state {
  padding: 12px;
  text-align: center;
  color: #b3b8c2;
  font-size: 13px;
  &.center { margin-top: 50px; }
}

.status-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f7fb;
  border-radius: 10px;
  padding: 12px 16px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 46px;
  height: 24px;
  flex-shrink: 0;
  input { opacity: 0; width: 0; height: 0; }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background: #ccd2dc;
    border-radius: 24px;
    transition: 0.2s;
    &::before {
      content: "";
      position: absolute;
      height: 18px; width: 18px;
      left: 3px; bottom: 3px;
      background: #fff;
      border-radius: 50%;
      transition: 0.2s;
    }
  }
  input:checked + .slider { background: #195BFF; }
  input:checked + .slider::before { transform: translateX(22px); }
}

.cod-summary { display: flex; gap: 12px; }
.summary-card {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  &.success { background: #e6f5ee; }
  &.danger  { background: #fdecec; }
}
.summary-icon { font-size: 18px; }
.summary-card.success .summary-icon { color: #22a06b; }
.summary-card.danger  .summary-icon { color: #e15454; }
.summary-label { font-size: 12px; color: #6b7080; }
.summary-value { font-size: 16px; font-weight: 600; color: #2c2f36; }
.unit { font-size: 12px; font-weight: 400; color: #8b8f99; }

.input-with-suffix {
  position: relative;
  .suffix {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #8b8f99;
    font-size: 13px;
  }
  .input { padding-right: 40px; }
}

.modal-footer { margin-top: 14px; }
.btn-submit {
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 10px;
  background: #195BFF;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #1450e6; }
  &.disabled, &:disabled {
    background: #cdd6eb;
    color: #fff;
    cursor: not-allowed;
  }
  &.loading { opacity: 0.7; cursor: progress; }
}
</style>

<!-- Non-scoped overrides for the underlying vs-dialog wrapper.
     :has() is used to target only the dialog containing this modal. -->
<style>
.vs-dialog:has(.advance-cod-wide-modal) {
  max-width: 760px !important;
  width: calc(100vw - 40px) !important;
}
.vs-dialog:has(.advance-cod-wide-modal) .vs-dialog__content {
  padding: 16px 20px !important;
}
.vs-dialog-content:has(.advance-cod-wide-modal) {
  padding-top: 40px !important;
  padding-bottom: 40px !important;
}
</style>
